/**
 * Role Definitions API + TanStack Query hooks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { RoleDefinition, UpdateRoleDefinitionInput } from '../types';

interface ListResponse {
  roles: RoleDefinition[];
}

export const roleDefinitionKeys = {
  all: ['teams', 'role-definitions'] as const,
  list: () => [...roleDefinitionKeys.all, 'list'] as const,
  detail: (role: string) => [...roleDefinitionKeys.all, 'detail', role] as const,
};

export const listRoleDefinitions = () =>
  api.get<ListResponse>('/v4/role-definitions');

export const updateRoleDefinition = (role: string, patch: UpdateRoleDefinitionInput) =>
  api.put<RoleDefinition>(`/v4/role-definitions/${encodeURIComponent(role)}`, patch);

export function useRoleDefinitions() {
  return useQuery({
    queryKey: roleDefinitionKeys.list(),
    queryFn: listRoleDefinitions,
  });
}

export function useUpdateRoleDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ role, patch }: { role: string; patch: UpdateRoleDefinitionInput }) =>
      updateRoleDefinition(role, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: roleDefinitionKeys.all });
      toast.success('Role documentation updated');
    },
    onError: (err: Error) =>
      toast.error('Failed to update role', { description: err.message }),
  });
}
